import { useMutation } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { atom, useSetAtom } from 'jotai';
import qs from 'qs';
import { memo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { object, string } from 'yup';
import { UiKitButton } from '@components/common/ui-kit-button';
import { UiKitInput } from '@components/common/ui-kit-input';
import { Container } from '@features/Unsubscribe/components/Container';
import { Title } from '@features/Unsubscribe/components/Title';
import { useUnsubscribeAnalytics } from '@features/Unsubscribe/hooks/use-unsubscribe-analytics';
import { Item, contentData } from '@features/Unsubscribe/steps/EmailEnter/components/Item';
import type { TpWizardSteps } from '@features/Unsubscribe/types';
import LoadingIndicator from '@components/common/loading-indicator/loading-indicator';
import styles from './EmailEnter.module.scss';
const FORM_SCHEMA = object({
  email: string().trim().required().email().lowercase()
});
const FORM_INITIAL_VALUES = {
  email: ''
};
export const emailAtom = atom('');
type TpEmailEnterProps = TpWizardSteps & {
  containerClassName?: string;
};
type TpItem = {
  email: string;
  targetTime: number;
};
const TIMER_GAP = 1000 * 60; // 1 minute

const verifyEmailMutation = async (email: string) => {
  const normalizedEmail = email.toLowerCase().trim();
  const formData = {
    email: normalizedEmail
  };
  await axios({
    method: 'post',
    url: `${process.env.NEXT_PUBLIC_API_MICROSERVICE_ENDPOINT}cancellation_verify_email`,
    data: qs.stringify(formData)
  });
};
export const EmailEnter = memo((props: TpEmailEnterProps) => {
  const {
    wizard
  } = props;
  const [isError, setIsError] = useState(false);
  const {
    formatMessage
  } = useIntl();
  const setEmail = useSetAtom(emailAtom);
  const {
    defaultEventOptions,
    trackUnsubscribeEvent
  } = useUnsubscribeAnalytics();
  const placeholderMessage = formatMessage({
    id: 'mail.hint',
    defaultMessage: 'Email'
  });
  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    // @ts-ignore
    validateOnMount,
    touched,
    errors,
    isValid,
    setFieldError
  } = useFormik({
    validateOnMount: true,
    initialValues: FORM_INITIAL_VALUES,
    validationSchema: FORM_SCHEMA,
    onSubmit: async values => {
      mutation.mutate(values.email);
    }
  });
  const mutation = useMutation({
    mutationFn: verifyEmailMutation,
    onSuccess: (_, email) => {
      const normalizedEmail = email.toLowerCase().trim();
      trackUnsubscribeEvent('cancel_email_enter_submitted', {
        email: normalizedEmail
      });
      checkEmail(normalizedEmail);
      setEmail(normalizedEmail);
      wizard.nextStep();
    },
    onError: (error: AxiosError, email) => {
      let reason = 'unknown';
      if (error.response?.status === 404) {
        reason = 'email_not_found';
        setFieldError('email', 'Email address not found');
        setIsError(true);
      } else if (error.code === 'NETWORK_ERROR') {
        reason = 'network_error';
        setFieldError('email', 'Network error. Please try again.');
        setIsError(true);
      } else {
        reason = 'unexpected_error';
        console.log(error);
        setFieldError('email', 'Something went wrong. Please try again.');
        setIsError(true);
      }
      trackUnsubscribeEvent('cancel_email_enter_failed', {
        email: email.toLowerCase().trim(),
        reason
      });
    }
  });
  const checkEmail = useCallback((email: string) => {
    const targetTime = Date.now() + TIMER_GAP;
    const newItem = {
      email,
      targetTime: `${targetTime}`
    };
    const userDataFromStorage = sessionStorage.getItem('userData');
    const parsedData = userDataFromStorage !== null ? JSON.parse(userDataFromStorage) : [];
    const findCurrent = parsedData.find((item: TpItem) => item.email === email && Number(item.targetTime) > Date.now());
    const userDataArray = [...(userDataFromStorage !== null ? [...parsedData] : []), ...(findCurrent !== undefined ? [] : [newItem])];
    sessionStorage.setItem('userData', JSON.stringify(userDataArray));
    return findCurrent;
  }, []);
  return <Container className={styles.container} eventOnMounted="cancel_email_enter_opened" eventOptions={defaultEventOptions}>
      <Title className={styles.title}>
        Enter the email you signed up with to cancel your subscription
      </Title>

      <div className={classnames(styles.limitation, 'double-grow')}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.emailGroup}>
            <UiKitInput autoFocus fullWidth inputMode="email" type="email" autoComplete="email" placeholder={placeholderMessage as string} name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.email && errors.email != null && isError} errorText={errors.email} errorTextClassName={styles.errorText} />
          </div>

          <div className="double-grow">
            {isError && <div className={styles.clarifications}>
                <h3 className={styles.limitationTitle}>What can you do?</h3>
                {contentData.map(item => <Item key={item.id} item={item} />)}
              </div>}
          </div>

          <UiKitButton type="submit" disabled={!isValid || mutation.isPending} className={styles.button} data-testid="submit" fullWidth>
            {mutation.isPending ? <LoadingIndicator /> : 'Submit'}
          </UiKitButton>
        </form>
      </div>
    </Container>;
});
EmailEnter.displayName = 'EmailEnter';